/**
 * Product Add-ons Cart Handler
 *
 * This script handles adding product add-ons to the cart.
 * Add-ons are identified by variant ID configured in the Liquid snippet.
 *
 * Default max per add-on if data-addon-quantity-max is missing (keep in sync with product-add-ons.liquid).
 */
const PRODUCT_ADDON_QUANTITY_MAX_DEFAULT = 10;

class ProductAddons {
  constructor() {
    this.mainProductVariantId = null;
    this.mainProductQuantity = 1;
    this.variantCache = {};
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Check if add-on options exist on this page
    const addonOptions = document.querySelectorAll('.product-add-ons .product-add-ons__option');
    
    if (addonOptions.length === 0) {
      return;
    }

    // Listen for changes to the add-on selectors
    this.setupAddons();

    // Monitor the product form for quantity/variant changes
    this.setupQuantityListener();

    // Load display names from live Shopify data by SKU
    this.loadAddonDisplayData();

    // Intercept add to cart form submission
    this.setupFormInterceptor();
  }

  getDisplayTitleFromVariantData(variantData, fallbackTitle = '') {
    const variantTitle = (variantData?.title || '').trim();
    const productTitle = (variantData?.productTitle || '').trim();
    const fallback = (fallbackTitle || '').trim();

    if (variantTitle && variantTitle.toLowerCase() !== 'default title') {
      return variantTitle;
    }
    if (fallback) {
      return fallback;
    }
    if (productTitle) {
      return productTitle;
    }
    return variantTitle || fallback || '';
  }

  sanitizeDescription(rawText = '', maxLength = 120) {
    if (!rawText) return '';

    const parser = document.createElement('div');
    parser.innerHTML = rawText;
    const plainText = (parser.textContent || parser.innerText || '').replace(/\s+/g, ' ').trim();

    if (!plainText) return '';
    if (plainText.length <= maxLength) return plainText;
    return `${plainText.slice(0, maxLength - 1)}…`;
  }

  getTotalAddonQuantity(rootEl = document) {
    let total = 0;
    rootEl.querySelectorAll('.product-add-ons [data-addon-quantity], [data-addon-quantity]').forEach((input) => {
      total += parseInt(input.value, 10) || 0;
    });
    return total;
  }

  getAddonQuantityMax(optionEl) {
    const root = optionEl?.closest('.product-add-ons');
    if (!root) return PRODUCT_ADDON_QUANTITY_MAX_DEFAULT;
    const raw = root.getAttribute('data-addon-quantity-max');
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n >= 1) return n;
    return PRODUCT_ADDON_QUANTITY_MAX_DEFAULT;
  }

  clampAddonQuantity(optionEl, value) {
    const max = this.getAddonQuantityMax(optionEl);
    const v = parseInt(value, 10) || 0;
    return Math.min(Math.max(0, v), max);
  }

  normalizeAddonQuantities(rootEl = document) {
    rootEl.querySelectorAll('.product-add-ons [data-addon-quantity], [data-addon-quantity]').forEach((input) => {
      const option = input.closest('.product-add-ons__option');
      const max = this.getAddonQuantityMax(option);
      const clamped = this.clampAddonQuantity(option, input.value);
      input.value = String(clamped);
      if (option) {
        option.classList.toggle('selected', clamped > 0);
      }
    });

    this.updateAddonState(rootEl);
  }

    /**
     * Setup add-on option controls
     */
    setupAddons() {
      if (this._addonDelegatedHandlersBound) return;
      this._addonDelegatedHandlersBound = true;

      document.addEventListener('click', (e) => {
        const selectBtn = e.target.closest('[data-addon-select-btn]');
        const qtyMinus = e.target.closest('[data-addon-qty-minus]');
        const qtyPlus = e.target.closest('[data-addon-qty-plus]');
        if (!selectBtn && !qtyMinus && !qtyPlus) return;

        const option = (selectBtn || qtyMinus || qtyPlus).closest('.product-add-ons__option');
        if (!option) return;
        const quantityInput = option.querySelector('[data-addon-quantity]');
        if (!quantityInput) return;

        if (selectBtn) {
          const isSelected = option.classList.contains('selected');
          const currentQuantity = parseInt(quantityInput.value, 10) || 0;
          const max = this.getAddonQuantityMax(option);

          if (isSelected) {
            quantityInput.value = '0';
            option.classList.remove('selected');
          } else {
            if (currentQuantity === 0) {
              quantityInput.value = String(Math.min(1, max));
            }
            option.classList.add('selected');
          }
          this.updateAddonState(option.closest('.product-add-ons'));
          return;
        }

        if (qtyMinus) {
          let value = parseInt(quantityInput.value, 10) || 0;
          if (value > 0) {
            value--;
            quantityInput.value = String(value);
            if (value === 0) option.classList.remove('selected');
            this.updateAddonState(option.closest('.product-add-ons'));
          }
          return;
        }

        if (qtyPlus) {
          const max = this.getAddonQuantityMax(option);
          let value = parseInt(quantityInput.value, 10) || 0;
          if (value < max) {
            value++;
            quantityInput.value = String(value);
            if (value > 0 && !option.classList.contains('selected')) {
              option.classList.add('selected');
            }
            this.updateAddonState(option.closest('.product-add-ons'));
          }
        }
      });
    }

    /**
     * Update internal state and trigger event
     */
    updateAddonState(rootEl = document) {
      const selected = this.getSelectedAddons(rootEl);
      this.hasAddon = selected.length > 0;
      this.updateCounter(rootEl);
      this.triggerCartUpdate(selected);
    }

    /**
     * Update the add-on counter display
     */
    updateCounter(rootEl = document) {
      const counterEl = rootEl.querySelector('[data-addon-counter]');
      if (counterEl) {
        counterEl.textContent = `${this.getTotalAddonQuantity(rootEl)} selected`;
      }
    }

    /**
     * Get all selected add-ons
     */
    getSelectedAddons(rootEl = document) {
      const addons = [];
      const optionEls = rootEl.querySelectorAll('.product-add-ons__option');

      optionEls.forEach(option => {
        const quantityInput = option.querySelector('[data-addon-quantity]');

        if (!quantityInput) return;

        const quantity = parseInt(quantityInput.value) || 0;
        if (quantity <= 0) return;

        const sku = option.dataset.addonSku || '';
        const title = option.dataset.addonTitle || '';
        
        addons.push({
          sku,
          title,
          price: parseInt(option.dataset.addonPrice || '0') || 0,
          quantity
        });
      });

      return addons;
    }

    async loadAddonDisplayData() {
      const optionEls = document.querySelectorAll('.product-add-ons__option');
      if (!optionEls.length) return;

      for (const option of optionEls) {
        const sku = option.dataset.addonSku || '';
        if (!sku) continue;

        try {
          const variantData = await this.getVariantDataBySku(sku);
          const liveTitle = this.getDisplayTitleFromVariantData(variantData, option.dataset.addonTitle || '');
          if (!liveTitle) continue;

          option.dataset.addonTitle = liveTitle;
          const titleEl = option.querySelector('.product-add-ons__option-title');
          if (titleEl) {
            titleEl.textContent = liveTitle;
          }
        } catch (error) {
          console.warn('[ProductAddons] Could not load live title for SKU:', sku, error);
        }
      }
    }

    /**
     * Notify other scripts / UI that add-on selection changed
     */
    triggerCartUpdate(selectedAddons = []) {
      const event = new CustomEvent('addon:updated', {
        detail: {
          addons: selectedAddons
        }
      });
      document.dispatchEvent(event);
    }

    /**
     * Listen for main product quantity changes
     */
    setupQuantityListener() {
      if (!this._quantityDelegatedHandlerBound) {
        this._quantityDelegatedHandlerBound = true;
        document.addEventListener('change', (e) => {
          const target = e.target;
          if (!(target instanceof HTMLInputElement)) return;
          const form = target.closest('form');
          if (!form) return;
          const addonRoot = form.querySelector('.product-add-ons');
          if (!addonRoot) return;

          if (target.name === 'quantity') {
            this.mainProductQuantity = parseInt(target.value, 10) || 1;
            this.normalizeAddonQuantities(addonRoot);
          }
          if (target.name === 'id') {
            this.mainProductVariantId = target.value;
          }
        });
      }

      document.querySelectorAll('.product-add-ons').forEach((container) => this.updateCounter(container));
    }

    /**
     * Intercept form submission to add add-ons to the cart
     */
    setupFormInterceptor() {
      if (this._submitInterceptorBound) return;
      this._submitInterceptorBound = true;

      document.addEventListener('submit', async (e) => {
          const form = e.target instanceof HTMLFormElement ? e.target : e.target?.closest?.('form');
          if (!form) return;
          const container = form.querySelector('.product-add-ons[data-product-form-id]')
            || (form.id ? document.querySelector(`.product-add-ons[data-product-form-id="${form.id}"]`) : null);
          if (!container) return;
          const selectedAddons = this.getSelectedAddons(container);
          if (selectedAddons.length === 0) return;

          e.preventDefault();
          e.stopPropagation();
          if (typeof e.stopImmediatePropagation === 'function') {
            e.stopImmediatePropagation();
          }
          if (form.dataset.addonsSubmitting === '1') {
            return;
          }
          form.dataset.addonsSubmitting = '1';

          const formData = new FormData(form);
          const mainProductId = formData.get('id');
          const mainProductQty = formData.get('quantity') || 1;
          const parsedMainQty = parseInt(mainProductQty, 10) || 1;
          const sortedAddonSkus = selectedAddons.map(a => a.sku).sort().join('-');
          const addonGroupId = `ag-${mainProductId}-${sortedAddonSkus}`;
          const parentProductTitle = (container.dataset?.parentProductTitle || '').trim();

          const submitButton = form.querySelector('[data-js-product-add-to-cart]') || form.querySelector('button[type="submit"]');
          if (submitButton) {
            submitButton.classList.add('working');
          }

          try {
            const mainProductProperties = {};
            for (const [key, value] of formData.entries()) {
              if (typeof key === 'string' && key.startsWith('properties[') && key.endsWith(']')) {
                const propName = key.slice(11, -1);
                if (propName) {
                  mainProductProperties[propName] = String(value ?? '');
                }
              }
            }

            const cartItems = [];

            const mainProductItem = {
              id: mainProductId,
              quantity: parsedMainQty,
              properties: {
                ...mainProductProperties,
                '_addon_group_id': addonGroupId,
                '_addon_parent_title': parentProductTitle
              }
            };
            const sellingPlanId = formData.get('selling_plan');
            if (sellingPlanId) {
              mainProductItem.selling_plan = String(sellingPlanId);
            }
            cartItems.push(mainProductItem);

            for (const addon of selectedAddons) {
              const variantData = await this.getVariantDataBySku(addon.sku);
              const addonVariantId = variantData?.id;
              const addonLiveTitle = this.getDisplayTitleFromVariantData(variantData, addon.title || '') || 'Add-on';

              if (!addonVariantId) {
                throw new Error(`Add-on "${addon.title}" could not be resolved from SKU "${addon.sku}".`);
              }

              cartItems.push({
                id: addonVariantId,
                quantity: addon.quantity,
                properties: {
                  '_addon_for_variant_id': mainProductId,
                  '_addon_label': addonLiveTitle,
                  '_addon_sku': addon.sku,
                  '_addon_group_id': addonGroupId,
                  '_addon_parent_title': parentProductTitle,
                  'Addon Note': addonLiveTitle
                }
              });
            }

            await this.addToCartMultipleItems(cartItems);
            await this.refreshCart();
            await this.openCartPopupAfterAdd(form);

            const productFormEl = form.closest('product-form');
            if (productFormEl) {
              productFormEl.dispatchEvent(new Event('add-to-cart'));
            }

            // Clear only the current form's add-on selections after successful add.
            this.normalizeAddonQuantities(container);
            container.querySelectorAll('[data-addon-quantity]').forEach((input) => {
              input.value = '0';
            });
            container.querySelectorAll('.product-add-ons__option.selected').forEach((option) => {
              option.classList.remove('selected');
            });
            this.updateAddonState(container);

            if (submitButton) {
              submitButton.classList.remove('working');
            }
            form.dataset.addonsSubmitting = '0';
          } catch (error) {
            console.error('[ProductAddons] Error adding to cart:', error);
            alert('There was an error adding items to your cart. Please try again.\n\nDetails: ' + error.message);

            if (submitButton) {
              submitButton.classList.remove('working');
            }
            form.dataset.addonsSubmitting = '0';
          }
      }, true);
    }

    async getVariantDataBySku(sku) {
      if (!sku) return null;
      if (this.variantCache[sku]) {
        return this.variantCache[sku];
      }

      let variantData = null;
      const accessToken = window.KROWN?.settings?.access_token || '';
      if (accessToken) {
        variantData = await this.lookupVariantDataBySkuGraphql(sku).catch(() => null);
      }

      if (!variantData) {
        variantData = await this.lookupVariantDataBySkuProductsJson(sku).catch(() => null);
      }

      if (!variantData) {
        throw new Error(`SKU "${sku}" was not found in storefront data.`);
      }

      this.variantCache[sku] = variantData;
      return variantData;
    }

    async lookupVariantDataBySkuGraphql(sku) {
      const query = `
        query ProductVariantBySku($query: String!) {
          productVariants(first: 1, query: $query) {
            edges {
              node {
                id
                sku
                title
                product {
                  title
                  description
                }
              }
            }
          }
        }
      `;

      const response = await fetch(`${window.Shopify.routes.root}api/2023-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.KROWN?.settings?.access_token || ''
        },
        body: JSON.stringify({
          query,
          variables: {
            query: `sku:${sku}`
          }
        })
      });

      if (!response.ok) return null;
      const data = await response.json();
      const node = data?.data?.productVariants?.edges?.[0]?.node;
      const rawId = node?.id || '';
      if (!rawId) return null;

      return {
        id: rawId.replace('gid://shopify/ProductVariant/', ''),
        sku: node?.sku || sku,
        title: node?.title || '',
        productTitle: node?.product?.title || '',
        productDescription: node?.product?.description || ''
      };
    }

    async lookupVariantDataBySkuProductsJson(sku) {
      let page = 1;
      const perPage = 250;
      const maxPages = 20;

      while (page <= maxPages) {
        const url = `${window.Shopify.routes.root}products.json?limit=${perPage}&page=${page}`;
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) break;

        const payload = await response.json();
        const products = Array.isArray(payload?.products) ? payload.products : [];
        if (!products.length) break;

        for (const product of products) {
          const variants = Array.isArray(product?.variants) ? product.variants : [];
          const matched = variants.find((variant) => (variant?.sku || '').trim() === sku);
          if (matched?.id) {
            return {
              id: String(matched.id),
              sku: matched.sku || sku,
              title: matched.title || '',
              productTitle: product?.title || '',
              productDescription: product?.body_html || ''
            };
          }
        }

        page += 1;
      }

      return null;
    }

    /**
     * Add multiple items to cart in a single request
     */
    async addToCartMultipleItems(items) {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: items
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || error.message || 'Failed to add items to cart');
      }

      return await response.json();
    }

    /**
     * Theme cart drawer / subtotal sync (delegates to component-cart.js when loaded).
     */
    async refreshCart() {
      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
        return;
      }

      if (typeof window.refreshCart === 'function') {
        await window.refreshCart();
        return;
      }

      console.warn('[ProductAddons] window.refreshCart is not available; load component-cart.js (theme cart_action popup/overlay).');
    }

    /**
     * When cart action is "popup", mirror ProductForm: load helper-cart-popup and open the drawer.
     */
    async openCartPopupAfterAdd(form) {
      if (typeof KROWN === 'undefined' || KROWN.settings?.cart_action !== 'popup') {
        return;
      }
      const productForm = form.closest('product-form');
      const productUrl = productForm?.dataset?.productUrl;
      const variantInput = form.querySelector('input[name="id"]');
      if (!productUrl || !variantInput?.value) {
        return;
      }
      try {
        const join = productUrl.includes('?') ? '&' : '?';
        const url = `${productUrl}${join}variant=${encodeURIComponent(String(variantInput.value))}&section_id=helper-cart-popup`;
        const r = await fetch(url, { cache: 'no-store' });
        if (!r.ok) {
          return;
        }
        const text = await r.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section =
          doc.querySelector('.shopify-section') ||
          doc.querySelector('[id^="shopify-section"]');
        const popupContent = document.querySelector('[data-js-site-cart-popup-content]');
        if (section && popupContent) {
          popupContent.innerHTML = section.innerHTML;
        }
        const sidebar = document.getElementById('site-cart-sidebar');
        if (sidebar && typeof sidebar.show === 'function') {
          sidebar.show();
        }
      } catch (e) {
        console.warn('[ProductAddons] Cart popup could not open:', e);
      }
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.ProductAddons = new ProductAddons();
  });
} else {
  window.ProductAddons = new ProductAddons();
}